import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#selectCghLinkedMedia();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #selectCghLinkedMedia() {
    const requestedMediaId = new URLSearchParams(window.location.search).get('cgh_media');
    if (!requestedMediaId) return;

    const normalizeMediaId = (value) => String(value || '').split('/').pop();
    const targetMediaId = normalizeMediaId(requestedMediaId);
    const getProductMedia = (element) => element?.querySelector?.('.product-media[data-media-id]') || null;
    const matchesTarget = (element) => normalizeMediaId(getProductMedia(element)?.dataset.mediaId) === targetMediaId;

    let attempts = 0;
    const maxAttempts = 12;

    const selectTarget = () => {
      if (!this.isConnected) return;

      const slideshow = this.slideshow;
      const slides = slideshow?.refs?.slides || [];
      const slideIndex = slides.findIndex(matchesTarget);
      const gridItems = Array.from(this.querySelectorAll('.media-gallery__grid > li'));
      const gridTarget = gridItems.find(matchesTarget);

      if (slideIndex >= 0 && slideshow) {
        const targetSlide = slides[slideIndex];
        const slideId = targetSlide.getAttribute('slide-id');

        if (slideId) slideshow.select({ id: slideId }, undefined, { animate: false });
        else slideshow.select(slideIndex, undefined, { animate: false });

        requestAnimationFrame(() => {
          const targetMedia = getProductMedia(targetSlide);
          const posterButton = targetMedia?.querySelector('[ref="deferredMediaPlayButton"]');
          const video = targetMedia?.querySelector('video');

          targetSlide.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          if (posterButton instanceof HTMLButtonElement) posterButton.click();
          else if (video instanceof HTMLVideoElement) video.play().catch(() => {});
        });

        return;
      }

      if (gridTarget) {
        const targetMedia = getProductMedia(gridTarget);
        const posterButton = targetMedia?.querySelector('[ref="deferredMediaPlayButton"]');
        const video = targetMedia?.querySelector('video');

        gridTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (posterButton instanceof HTMLButtonElement) posterButton.click();
        else if (video instanceof HTMLVideoElement) video.play().catch(() => {});
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) requestAnimationFrame(selectTarget);
    };

    requestAnimationFrame(selectTarget);
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
